import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import queryString from 'query-string';
import { Route, Switch, Redirect } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import CssBaseline from '@material-ui/core/CssBaseline';
import { APP_MAIN_TYPE, LOGIN_COMPONENT_TYPE, IDLE_DIALOG_TYPE } from '../../services/ViewService';
import ErrorSnackbar from './ErrorSnackbar';

import '../../assets/fonts/index.css';
import './App.scss';

const isExternalURL = (path) => {
  const reg = /^https?:\/\//i;
  return path && reg.test(path);
};

const LoginRedirect = ({ path, location }) => {
  if (isExternalURL(path) || (path === '/')) {
    window.location.href = path;
    return null;
  }
  return (
    <Redirect to={{
        pathname: path,
        state: { referrer: location },
      }}
    />
  );
};

LoginRedirect.propTypes = {
  path: PropTypes.string.isRequired,
  location: PropTypes.shape({}).isRequired,
};

class App extends React.Component {
  static propTypes = {
    match: PropTypes.shape({
      params: PropTypes.shape({}),
      url: PropTypes.string.isRequired,
    }).isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
      search: PropTypes.string,
    }).isRequired,
    history: PropTypes.shape({ push: PropTypes.func }).isRequired,
    configuration: PropTypes.shape({
      prefix: PropTypes.string,
      services: PropTypes.shape({}).isRequired,
      getServices: PropTypes.func.isRequired,
      viewService: PropTypes.shape({}).isRequired,
      routeService: PropTypes.shape({}).isRequired,
      schemaService: PropTypes.shape({}),
      authService: PropTypes.shape({
        subscribe: PropTypes.func,
        unsubscribe: PropTypes.func,
      }),
      getRoutes: PropTypes.func,
      getApplicationTitle: PropTypes.func,
    }).isRequired,
    jsonConfig: PropTypes.shape({
      app: PropTypes.shape({
        className: PropTypes.string,
        idleTimeout: PropTypes.number,
        idleWarningTimeout: PropTypes.number,
        logoutPath: PropTypes.string,
      }),
    }),
  }

  static defaultProps = {
    jsonConfig: undefined,
  }

  constructor(props) {
    super(props);
    // Snackbar
    this.errorSnackbarQueue = [];

    this.onError = this.onError.bind(this);
    this.closeErrorSnackbar = this.closeErrorSnackbar.bind(this);
    this.onExitErrorSnackbar = this.onExitErrorSnackbar.bind(this);

    // Services, after binding as binded methods used for services
    const config = props.configuration;
    this.viewService = config.viewService;
    this.routeService = config.routeService;
    this.schemaService = config.schemaService;
    this.authService = config.authService;
    this.editProfilePath = this.routeService.getEditProfilePath();

    config.getServices().forEach((service) => {
      if (service.setErrorHandler) {
        service.setErrorHandler(this.onError);
      }
    });
    const { jsonConfig } = props;
    this.className = jsonConfig && jsonConfig.app && jsonConfig.app.className;
    this.idleTimeout = jsonConfig && jsonConfig.app && jsonConfig.app.idleTimeout;
    this.idleWarningTimeout = jsonConfig && jsonConfig.app && jsonConfig.app.idleWarningTimeout;
    this.logoutPath = jsonConfig && jsonConfig.app && jsonConfig.app.logoutPath;

    this.onAuthChange = ({ isAuth }) => {
      if (!isAuth) {
        console.log('onAuthChange: ', new Date().getTime());
        // User login expired
        const loginPath = this.routeService.getLoginPath(this.props);
        if (this.props.location.pathname !== loginPath) {
          // https://github.com/ReactTraining/react-router/issues/1434
          if (isExternalURL(loginPath)) {
            console.log('    external: ', new Date().getTime());
            window.location.href = loginPath;
          } else {
            console.log('    internal: ', new Date().getTime());
            this.props.history.push(loginPath, { referrer: { pathname: this.props.location.pathname } });
          }
        }
      }
    };
    this.onAuthChange = this.onAuthChange.bind(this);
    this.onEditProfile = this.onEditProfile.bind(this);
    this.onProfileChanged = this.onProfileChanged.bind(this);
    this.authService.subscribe(this.onAuthChange);
    // App state
    this.state = {
      showErrorSnackbar: false,
      errorOnSnackbar: null,
      loading: !this.authService.isInitialized(),
    };
    if (this.state.loading) {
      this.authService.whenInitialized().then(() => {
        this.setState({
          loading: false,
        });
      });
    }

    // Routes initialization
    this.AppMain = this.viewService.getComponent(APP_MAIN_TYPE);
    this.LoginComponent = this.viewService.getComponent(LOGIN_COMPONENT_TYPE);
    this.IdleDialog = !!this.idleTimeout && this.viewService.getComponent(IDLE_DIALOG_TYPE);

    // Constant props to be propagated to child components
    this.appProps = {
      ...config.services,
      routes: config.getRoutes(),
      prefix: config.prefix,
      onEditProfile: this.onEditProfile,
      onProfileChanged: this.onProfileChanged,
    };

    this.mounted = false;
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.authService.unsubscribe(this.onAuthChange);
    this.mounted = false;
  }

  onEditProfile() {
    this.props.history.push(this.editProfilePath, { referrer: this.props.location });
  }

  onProfileChanged() {
    this.setState({});
  }

  onExitErrorSnackbar() {
    if (this.errorSnackbarQueue.length > 0) {
      this.setState({
        errorOnSnackbar: this.errorSnackbarQueue.shift(),
        showErrorSnackbar: true,
      });
    }
  }

  onError(error) {
    const processedError = this.processError(error);
    if (!this.mounted) {
      console.log(processedError.message || processedError);
    } else if (this.state.showErrorSnackbar) {
      this.errorSnackbarQueue.push(processedError);
    } else {
      this.setState({ showErrorSnackbar: true, errorOnSnackbar: processedError });
    }
  }

  processError(error) {
    // Process error if it contains an error object, generated by the optimization server
    if (!error.response || !error.response.data || !error.response.data.error) {
      return error;
    }
    const processedError = Object.assign({}, error.response.data.error);
    if (processedError.code) {
      processedError.message = `[${processedError.code}] ${processedError.message}`;
    }
    return processedError;
  }

  closeErrorSnackbar() {
    this.setState({ showErrorSnackbar: false, errorOnSnackbar: null });
  }

  render() {
    const { location, configuration } = this.props;
    // this.props is given a location property by the enclosing router to this App container.
    // Propagating the location property allows for child components to detect location changes
    // for re-rendering if other properties are not used by these components.
    // Details here: https://github.com/ReactTraining/react-router/blob/master/packages/react-router/docs/guides/blocked-updates.md
    const props = {
      ...this.props,
      ...this.appProps,
      query: queryString.parse(location.search),
      username: this.authService.getUsername(),
      profile: this.authService.getProfile(),
    };
    const loginPath = this.routeService.getLoginPath(this.props);
    return (
      <React.Fragment>
        <CssBaseline />
        <div className={classnames('tuc-root', this.className)}>
          {this.state.loading && (
            <div className="tuc-loading-panel">
              <Typography variant="body1">Access User profile</Typography>
              <LinearProgress style={{ width: '100%' }} />
            </div>
          )}
          {!this.state.loading && (
            <Switch>
              {loginPath && (loginPath !== '/') && (
                <Route
                  path={loginPath}
                  render={() => (
                    <this.LoginComponent {...props} title={configuration.getApplicationTitle()} />
                  )}
                />
              )}
              <Route render={() => {
                  // Having <LoginRedirect ...> as sibling to this <Route ...> lead to get the render method or this route
                  // invoked even if this.authService.isAuthenticated() === false
                  // => If the URL was an ADMIN protected URL, it was filtered in this extraneous <Route render> which lead
                  // the redirect to the login panel to point to a default valid URL instead of the initial ADMIN URL.
                  // We were loosing the initial referrer URL (tests on ADMIN pages were failing)
                  if (!this.authService.isAuthenticated()) {
                    return <LoginRedirect path={loginPath} location={props.location} />;
                  }
                  return <this.AppMain {...props} />;
                }}
              />
            </Switch>
          )}
          <ErrorSnackbar
            error={this.state.errorOnSnackbar}
            open={this.state.showErrorSnackbar}
            onClose={this.closeErrorSnackbar}
            onExitSnackbar={this.onExitErrorSnackbar}
          />
          {this.idleTimeout && this.IdleDialog && (
            <this.IdleDialog
              timeout={this.idleTimeout}
              warningTimeout={this.idleWarningTimeout || undefined}
              logoutPath={this.logoutPath}
            />
          )}
        </div>
      </React.Fragment>
    );
  }
}

export default App;
